import { ConnectedSelectBox } from '@eti/form';
import { useProperty, useTranslation } from '@eti/providers';
import { Inline, SelectBox, Text } from '@etg/wings';
import { constants, price as priceUtils } from '@eti/utils';
import { useRef, useState } from 'react';
import { Notification, variants } from '@eti/components';
import { useLinksAccessibility } from '../../../custom-hooks/useLinkAccessibility';
import { screenReaderOnly } from '../../../common/utils/styles';
import { formatLabel, formatOptions, getMinPrice, getTotalPrice, handleSelectedOption } from '../../../common/utils/selectBoxOptions';
import ProductLayout from '../../../common/main-layout/ProductLayout';
import getTranslatedTexts from '../../../common/text-resolvers/utils/getTranslatedTexts';
import { CHECK_IN_BAGGAGE } from '../../../products';
import { useIllustration } from '../../../common/utils/useIllustration';
import { getBaggageWeight, getTravelersPostbooking } from '../utils/checkInBaggageUtils';
import { getReadMoreLabel, getSingleChoiceProps } from '../../../common/utils/productCardProps';
import { useProductSnowplowTracking } from '../../../custom-hooks/useProductSnowplowTracking';
import { useMutationObserver } from '../../../custom-hooks/useMutationObserver';
import { checkInBaggagePostbookingStyles } from './styles';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const generalTexts = {
  additionalInformation: 'checkInBaggageHeaderAdditionalInformation',
  allTravelersLabel: 'Product.BaggagePerTraveler.Dropdown.Label.Select.All',
  fromPriceLabel: 'Products.Price.Label.From',
  infantNote: 'Product.Infant.Note',
  noTravelersLabel: 'Product.BaggagePerTraveler.Dropdown.Label.Select.None',
  onlyPriceLabel: 'Products.Price.Label.Only',
  pieces: 'Product.BaggagePerTraveler.Pieces',
  placeholder: 'Product.BaggagePerTraveler.Dropdown.Placeholder.Text',
  placeholderSelectedPlural: 'Product.BaggagePerTraveler.Dropdown.Label.Selected.Plural',
  placeholderSelectedSingular: 'Product.BaggagePerTraveler.Dropdown.Label.Selected.Singular',
  promotionalText: 'checkInBaggagePromotionalText',
  readMoreContent: 'checkInBaggageReadMoreModalContent',
  selectboxLabel: 'Product.BaggagePerTraveler.Dropdown.Label.Text',
  title: 'checkInBaggageName',
  totalPriceLabel: 'Products.Price.Label.Total',
  weightValues: 'Product.BaggagePerTraveler.Weight',
  weightDescription: 'Product.BaggagePerTraveler.Weight.Description',
  dropDownCloseInstruction: 'ScreenReader.Product.CloseDropdown.Instruction.Text'
};
const {
  checkInBaggageProductLayoutStyles,
  checkInBaggageBodyGridLayoutStyles,
  checkInBaggageCtaGridLayoutStyles,
  weightDescriptionGridStyle,
  notificationWrapperStyles,
  connectedSelectBoxStyles,
  selectBoxOptionListStyle,
  selectBoxOptionLabelWrapperStyle,
  selectBoxOptionLabelTextStyle
} = checkInBaggagePostbookingStyles();
const CheckInBaggagePostbooking = _ref => {
  var _order$travelers, _order$availableExtra, _sellSpecification$se, _sellSpecification$se2;
  let {
    order
  } = _ref;
  const [selectedCheckInBagOptions, setSelectedCheckInBagOptions] = useState([]);
  const [selectedSingleChoiceValue, setSelectedSingleChoiceValue] = useState(undefined);
  const {
    t,
    nonDebugT
  } = useTranslation();
  const {
    p
  } = useProperty();
  const rootElement = useRef(null);
  useProductSnowplowTracking(CHECK_IN_BAGGAGE.name, {
    rootElement
  });
  const illustrationUrl = useIllustration(CHECK_IN_BAGGAGE.name);
  const {
    additionalInformation,
    allTravelersLabel,
    infantNote,
    fromPriceLabel,
    noTravelersLabel,
    onlyPriceLabel,
    placeholder,
    placeholderSelectedPlural,
    placeholderSelectedSingular,
    promotionalText,
    readMoreContent,
    selectboxLabel,
    title,
    totalPriceLabel,
    dropDownCloseInstruction
  } = getTranslatedTexts(generalTexts, t);
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: title
  });
  const travelers = getTravelersPostbooking(CHECK_IN_BAGGAGE.id, order);
  const travelersWithoutInfants = travelers === null || travelers === void 0 ? void 0 : travelers.filter(_ref2 => {
    let {
      ageType
    } = _ref2;
    return ageType !== constants.ageTypes.INFANT;
  });
  const showSingleChoiceSelectButtons = (travelersWithoutInfants === null || travelersWithoutInfants === void 0 ? void 0 : travelersWithoutInfants.length) === 1;
  const hasInfants = Boolean(order === null || order === void 0 || (_order$travelers = order.travelers) === null || _order$travelers === void 0 ? void 0 : _order$travelers.some(traveler => traveler.ageType === constants.ageTypes.INFANT));
  const checkInBagOptions = travelersWithoutInfants === null || travelersWithoutInfants === void 0 ? void 0 : travelersWithoutInfants.map(_ref3 => {
    let {
      id,
      name,
      price
    } = _ref3;
    return {
      id: id || '',
      name,
      priceRaw: price
    };
  });
  const sellSpecification = (_order$availableExtra = order.availableExtraProducts.find(product => Number(product.id) === CHECK_IN_BAGGAGE.id)) === null || _order$availableExtra === void 0 ? void 0 : _order$availableExtra.sellSpecification;
  const {
    options = []
  } = (_sellSpecification$se = sellSpecification === null || sellSpecification === void 0 || (_sellSpecification$se2 = sellSpecification.sellPriceBaggage) === null || _sellSpecification$se2 === void 0 ? void 0 : _sellSpecification$se2[0]) !== null && _sellSpecification$se !== void 0 ? _sellSpecification$se : {};
  const {
    weight = 0,
    numberOfUnits: numberOfPieces = 0,
    weightUnit = ''
  } = options[0] || {};
  const formattedOptions = formatOptions(checkInBagOptions, allTravelersLabel, noTravelersLabel);
  const isSelected = selectedCheckInBagOptions && (selectedCheckInBagOptions === null || selectedCheckInBagOptions === void 0 ? void 0 : selectedCheckInBagOptions.length) > 0;
  const getPriceLabel = () => {
    if (isSelected) {
      return totalPriceLabel;
    }
    return showSingleChoiceSelectButtons ? onlyPriceLabel : fromPriceLabel;
  };
  const productPriceRaw = isSelected ? getTotalPrice(selectedCheckInBagOptions === null || selectedCheckInBagOptions === void 0 ? void 0 : selectedCheckInBagOptions.filter(option => !(option !== null && option !== void 0 && option.exclude))) : getMinPrice(checkInBagOptions);
  const extraProps = {
    additionalBaggageWeight: getBaggageWeight({
      numberOfPieces,
      weight,
      weightUnit,
      texts: {
        pieces: t(generalTexts.pieces, numberOfPieces),
        weightValues: t(generalTexts.weightValues, weight, weightUnit),
        weightDescription: arg => t(generalTexts.weightDescription, arg)
      }
    }),
    ...(showSingleChoiceSelectButtons ? {
      ...getSingleChoiceProps(t, p, nonDebugT),
      normalize: value => {
        setSelectedSingleChoiceValue(value);
        if (value === 'true') {
          setSelectedCheckInBagOptions(formattedOptions);
          return formattedOptions;
        }
        setSelectedCheckInBagOptions([]);
        return [];
      },
      selectedValue: selectedSingleChoiceValue
    } : {})
  };
  const readMoreButtonLabel = getReadMoreLabel(t);
  const productData = {
    additionalInformation,
    description: '',
    extraProps,
    illustrationUrl,
    isSelected,
    price: priceUtils.formatPrice(productPriceRaw),
    priceLabel: getPriceLabel(),
    priceRaw: productPriceRaw !== null && productPriceRaw !== void 0 ? productPriceRaw : 0,
    productId: CHECK_IN_BAGGAGE.id,
    productName: CHECK_IN_BAGGAGE.name,
    promotionalText,
    readMoreButtonLabel,
    readMoreContent,
    title,
    isHighlighted: false,
    shouldDisplayUpsell: false
  };
  const formatSelectedOption = selectedOptions => {
    const label = formatLabel({
      selectedOptions,
      allTravelersLabel,
      defaultPlaceholder: placeholder,
      placeholderSelectedSingular,
      placeholderSelectedPlural,
      noTravelersLabel
    });
    return /*#__PURE__*/_jsx("p", {
      children: label
    });
  };

  // Add ARIA roles and attributes to enhance screen reader usability when the dropdown menu opens.
  useMutationObserver({
    targetElement: rootElement,
    selector: "[role='listbox']",
    onElementAdded: element => {
      element.setAttribute('aria-describedby', 'dropdown-close-instruction');
    }
  });
  return /*#__PURE__*/_jsxs(ProductLayout, {
    customGrid: checkInBaggageProductLayoutStyles(readMoreContent),
    productData: productData,
    ref: rootElement,
    children: [/*#__PURE__*/_jsx(ProductLayout.Body, {
      customGrid: checkInBaggageBodyGridLayoutStyles,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [extraProps.additionalBaggageWeight && /*#__PURE__*/_jsx("bdi", {
          className: weightDescriptionGridStyle,
          "data-testid": `${CHECK_IN_BAGGAGE.name}-weight-description`,
          children: extraProps.additionalBaggageWeight
        }), hasInfants && infantNote ? /*#__PURE__*/_jsx(Notification, {
          className: notificationWrapperStyles,
          dataTestId: `${CHECK_IN_BAGGAGE.name}-infant-notification`,
          variant: variants.INFORMATION,
          children: infantNote
        }) : null]
      })
    }), !showSingleChoiceSelectButtons ? /*#__PURE__*/_jsx(ProductLayout.Cta, {
      customGrid: checkInBaggageCtaGridLayoutStyles,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          "aria-hidden": true,
          className: screenReaderOnly,
          id: "dropdown-close-instruction",
          tabIndex: -1,
          children: dropDownCloseInstruction
        }), /*#__PURE__*/_jsx(ConnectedSelectBox, {
          className: connectedSelectBoxStyles,
          "data-testid": `${CHECK_IN_BAGGAGE.name}-select-box`,
          formatSelectedOption: formatSelectedOption,
          isDisabled: !(formattedOptions !== null && formattedOptions !== void 0 && formattedOptions.length),
          isMultiple: true,
          label: selectboxLabel,
          name: `${CHECK_IN_BAGGAGE.name}.value`,
          normalize: (newValue, prevVal) => {
            const selectedOption = handleSelectedOption(formattedOptions, prevVal, newValue);
            setSelectedCheckInBagOptions(selectedOption);
            return selectedOption;
          },
          placeholder: placeholder,
          validationRuleName: CHECK_IN_BAGGAGE.name,
          children: /*#__PURE__*/_jsx("ul", {
            className: selectBoxOptionListStyle,
            children: formattedOptions === null || formattedOptions === void 0 ? void 0 : formattedOptions.map((option, index) => {
              var _String;
              return /*#__PURE__*/_jsx(SelectBox.Option, {
                "data-testid": `etiDropdownOption-${CHECK_IN_BAGGAGE.name}-option-${(_String = String(option.value)) === null || _String === void 0 ? void 0 : _String.toLocaleLowerCase()}`,
                isMultiple: true,
                label: option.label ? /*#__PURE__*/_jsxs(Inline, {
                  className: selectBoxOptionLabelWrapperStyle,
                  children: [/*#__PURE__*/_jsx(Text, {
                    variant: index === 0 ? '14-bold' : '14',
                    children: option.label
                  }), /*#__PURE__*/_jsx(Text, {
                    className: selectBoxOptionLabelTextStyle,
                    "data-testid": `etiDropdownOption-${CHECK_IN_BAGGAGE.name}-price-selection-${index}`,
                    variant: index === 0 ? '14-bold' : '14',
                    children: option.price
                  })]
                }) : '',
                value: option
              }, option.value);
            })
          })
        })]
      })
    }) : null]
  });
};
export default CheckInBaggagePostbooking;
//# sourceMappingURL=CheckInBaggagePostbooking.js.map